/**
 * Storm's adaptive Huffman coder (MPQ sector compression 0x01), decode side only.
 * Algorithm and initial weight tables follow StormLib's huff.cpp —
 * Copyright (c) 1999-2013 Ladislav Zezula, MIT License. See THIRD_PARTY.md.
 *
 * The tree is an FGK-style adaptive Huffman tree: nodes live in a doubly linked list ordered
 * by weight (list head = sentinel 0), and weights are bumped and siblings swapped after
 * each decoded symbol so the encoder and decoder trees stay in lock-step.
 */

// Initial byte weights for the nine data types (hex, 256 bytes each).
const WEIGHTS = [
  '0a000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000002', // Sparse
  '5416160d0c08060506050603040403050e0b141313090b0605040302030202020d070906060403020403030303030202090604040404030203020202020302040803040709050303030302020203020203020202020202020201010102010202060a0808060704030404020204020303040307070906040303020102020202020a0202030202010102020206030502030201010101010101010101020301010102010101010101020404040709080c02010101010101010101010101020101030401020405010101010101010201010104010101010102010101010101010101020101010101010103010101010101010201010101010102020101020202064b', // Binary
  '0000000000000000000327000023000000000000000000000000000000000000ff0101010101010102020101060e10040608050404030302020303010102010101040204020202010104010102030302030103060401010101010102010201010129071612400a0a112501031710262a100123232f10060702090101010101000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000', // Text
  'ff0b07050b020202060202010402010309010101030401010201010102010101050101010d0101010101010101010101020101030101010101010102010101010a0402010603020101010101030101010502030403030302010101020102030301030101020501010403050103010303020104030a06010101010101010101010202010a0205010102070217010501010e010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010106020104050101020101010102010101010101010101010101010101010101010101010101010101070101020101010102010101010101010201010101010111', // General
  'fffb989a848563643e3e222213131817000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000', // ADPCM_4
  'fff19d9e9a9b9a9793938c8e868880827c7c7273696b5f6055564a4b404137372f2f272721211b1c1717131310100d0d0b0b0909080807070605050404041918000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000', // ADPCM_6
  'c3cbf541ff7bf7210000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000bfccf240fd7cf72200000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000007a46000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000', // Stereo_3
  'c3d9ef3df97ce91efdabf12cfc5bfe17000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000bdd9ec3df57de81dfbaef02cfb5cff18000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000706c000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000', // Stereo_4
  'bac5da33e36dd818e594da23df4ad110eeafe42cea5ade15f487e921f643fc120000000000000000000000000000000000000000000000000000000000000000b0c7d833e36bd618e795d823db49d011e9b2e22be85cdd15f187e720f744ff1300000000000000000000000000000000000000000000000000000000000000005f9e000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000', // Stereo_5
].map((hex) => Uint8Array.from(hex.match(/../g)!, (h) => parseInt(h, 16)));

const POOL = 516; // 515 items + sentinel
const END = 0x100;
const NEW_SYMBOL = 0x101;

class AdaptiveTree {
  readonly next = new Int32Array(POOL).fill(-1);
  readonly prev = new Int32Array(POOL).fill(-1);
  readonly parent = new Int32Array(POOL).fill(-1);
  readonly childLo = new Int32Array(POOL).fill(-1);
  readonly value = new Int32Array(POOL);
  readonly weight = new Uint32Array(POOL);
  readonly byByte = new Int32Array(0x102).fill(-1);
  private used = 1;

  constructor() {
    this.next[0] = this.prev[0] = 0; // empty circular list
  }

  private unlink(i: number): void {
    if (this.next[i] === -1) return;
    this.next[this.prev[i]] = this.next[i];
    this.prev[this.next[i]] = this.prev[i];
    this.next[i] = this.prev[i] = -1;
  }

  /** Inserts `b` immediately after `a`. */
  private linkAfter(a: number, b: number): void {
    const n = this.next[a];
    this.next[b] = n;
    this.prev[b] = this.prev[n];
    this.prev[n] = b;
    this.next[a] = b;
  }

  private findHigherOrEqual(i: number, w: number): number {
    if (i === -1) return 0;
    while (i !== 0) {
      if (this.weight[i] >= w) return i;
      i = this.prev[i];
    }
    return 0;
  }

  private create(value: number, weight: number, after: boolean): number {
    const i = this.used++;
    if (after) this.linkAfter(0, i);
    else this.linkAfter(this.prev[0], i);
    this.value[i] = value;
    this.weight[i] = weight;
    this.parent[i] = -1;
    this.childLo[i] = -1;
    return i;
  }

  private fixup(i: number, maxWeight: number): number {
    if (this.weight[i] < maxWeight) {
      const h = this.findHigherOrEqual(this.prev[0], this.weight[i]);
      this.unlink(i);
      this.linkAfter(h, i);
      return maxWeight;
    }
    return this.weight[i];
  }

  build(type: number): void {
    const table = WEIGHTS[type & 0x0f];
    if (!table) throw new Error(`huffman: bad data type ${type}`);
    let maxW = 0;
    for (let b = 0; b < 256; b++) {
      if (table[b] === 0) continue;
      const i = this.create(b, table[b], true);
      this.byByte[b] = i;
      maxW = this.fixup(i, maxW);
    }
    this.byByte[END] = this.create(END, 1, false);
    this.byByte[NEW_SYMBOL] = this.create(NEW_SYMBOL, 1, false);
    let lo = this.prev[0];
    while (lo !== 0) {
      const hi = this.prev[lo];
      if (hi === 0) break;
      const n = this.create(0, this.weight[hi] + this.weight[lo], true);
      this.parent[lo] = n;
      this.parent[hi] = n;
      this.childLo[n] = lo;
      maxW = this.fixup(n, maxW);
      lo = this.prev[hi];
    }
  }

  incWeights(item: number): void {
    for (; item !== -1; item = this.parent[item]) {
      this.weight[item]++;
      const higher = this.findHigherOrEqual(this.prev[item], this.weight[item]);
      const childHi = this.next[higher];
      if (childHi === item) continue;
      this.unlink(childHi);
      this.linkAfter(item, childHi);
      this.unlink(item);
      this.linkAfter(higher, item);
      const chLo = this.childLo[this.parent[childHi]];
      let p = this.parent[item];
      if (this.childLo[p] === item) this.childLo[p] = childHi;
      if (chLo === childHi) this.childLo[this.parent[childHi]] = item;
      p = this.parent[item];
      this.parent[item] = this.parent[childHi];
      this.parent[childHi] = p;
    }
  }

  insertBranch(v1: number, v2: number): void {
    const last = this.prev[0];
    const hi = this.create(v1, this.weight[last], false);
    this.parent[hi] = last;
    this.byByte[v1] = hi;
    const lo = this.create(v2, 0, false);
    this.parent[lo] = last;
    this.childLo[last] = lo;
    this.byByte[v2] = lo;
    this.incWeights(lo);
  }

  get first(): number {
    return this.next[0];
  }
  get lastValue(): number {
    return this.value[this.prev[0]];
  }
}

export function decompressHuffman(input: Uint8Array, outLen: number): Uint8Array {
  const out = new Uint8Array(outLen);
  if (outLen === 0 || input.length === 0) return out;
  let ip = 0;
  let bitBuf = 0;
  let bitCnt = 0;
  const bit = (): number => {
    if (bitCnt === 0) {
      if (ip >= input.length) return -1;
      bitBuf = input[ip++];
      bitCnt = 8;
    }
    const b = bitBuf & 1;
    bitBuf >>>= 1;
    bitCnt--;
    return b;
  };
  const byte = (): number => {
    if (bitCnt < 8) {
      if (ip >= input.length) return -1;
      bitBuf |= input[ip++] << bitCnt;
      bitCnt += 8;
    }
    const v = bitBuf & 0xff;
    bitBuf >>>= 8;
    bitCnt -= 8;
    return v;
  };

  const type = byte();
  const sparse = type === 0;
  const t = new AdaptiveTree();
  t.build(type);
  let op = 0;
  for (;;) {
    let item = t.first;
    if (item === 0) break;
    let ok = true;
    while (t.childLo[item] !== -1) {
      const b = bit();
      if (b < 0) {
        ok = false;
        break;
      }
      item = b ? t.prev[t.childLo[item]] : t.childLo[item];
    }
    if (!ok) break;
    let v = t.value[item];
    if (v === END) break;
    if (v === NEW_SYMBOL) {
      v = byte();
      if (v < 0) break;
      t.insertBranch(t.lastValue, v);
      if (!sparse) t.incWeights(t.byByte[v]);
    }
    if (op >= outLen) break;
    out[op++] = v;
    if (sparse) t.incWeights(t.byByte[v]);
  }
  return op === outLen ? out : out.subarray(0, op);
}
